import type { InputCommand } from "@neon-fuse/game-core";

export type ControlAction =
  "up" | "down" | "left" | "right" | "bomb" | "detonate";

const KEY_BINDINGS: Readonly<Record<string, ControlAction>> = Object.freeze({
  ArrowUp: "up",
  w: "up",
  W: "up",
  ArrowDown: "down",
  s: "down",
  S: "down",
  ArrowLeft: "left",
  a: "left",
  A: "left",
  ArrowRight: "right",
  d: "right",
  D: "right",
  " ": "bomb",
  Enter: "bomb",
  e: "detonate",
  E: "detonate",
});

export class InputController {
  private readonly held = new Set<ControlAction>();
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const action = KEY_BINDINGS[event.key];
    if (action === undefined) return;
    event.preventDefault();
    this.held.add(action);
  };
  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const action = KEY_BINDINGS[event.key];
    if (action === undefined) return;
    event.preventDefault();
    this.held.delete(action);
  };

  public attach(): void {
    window.addEventListener("keydown", this.onKeyDown, { passive: false });
    window.addEventListener("keyup", this.onKeyUp, { passive: false });
  }

  public detach(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.held.clear();
  }

  public set(action: ControlAction, pressed: boolean): void {
    if (pressed) this.held.add(action);
    else this.held.delete(action);
  }

  public clear(): void {
    this.held.clear();
  }

  public command(): InputCommand {
    const gamepad = this.gamepadCommand();
    return {
      up: this.held.has("up") || gamepad.up === true,
      down: this.held.has("down") || gamepad.down === true,
      left: this.held.has("left") || gamepad.left === true,
      right: this.held.has("right") || gamepad.right === true,
      bomb: this.held.has("bomb") || gamepad.bomb === true,
      detonate: this.held.has("detonate") || gamepad.detonate === true,
    };
  }

  private gamepadCommand(): InputCommand {
    if (
      typeof navigator === "undefined" ||
      typeof navigator.getGamepads !== "function"
    )
      return {};
    const gamepad = Array.from(navigator.getGamepads()).find(
      (candidate): candidate is Gamepad =>
        candidate !== null && candidate.connected,
    );
    if (gamepad === undefined) return {};
    const horizontal = gamepad.axes[0] ?? 0;
    const vertical = gamepad.axes[1] ?? 0;
    return {
      left: horizontal < -0.35,
      right: horizontal > 0.35,
      up: vertical < -0.35,
      down: vertical > 0.35,
      bomb: gamepad.buttons[0]?.pressed === true,
      detonate: gamepad.buttons[1]?.pressed === true,
    };
  }
}
